import React, { useState } from 'react';
import { Calendar, ChevronDown, Printer, RefreshCw } from 'lucide-react';

export type PeriodType = 'today' | 'yesterday' | '7d' | '30d' | 'this_month' | 'last_month' | 'custom';

interface Props {
  selectedPeriod: PeriodType;
  onSelectPeriod: (period: PeriodType, customFrom?: string, customTo?: string) => void;
  customFrom?: string;
  customTo?: string;
  isComparing: boolean;
  onToggleCompare: (comparing: boolean) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const PERIOD_OPTIONS: { id: PeriodType; label: string }[] = [
  { id: 'today',      label: 'Hoy' },
  { id: 'yesterday',  label: 'Ayer' },
  { id: '7d',         label: 'Últimos 7 días' },
  { id: '30d',        label: 'Últimos 30 días' },
  { id: 'this_month', label: 'Este Mes' },
  { id: 'last_month', label: 'Mes Anterior' },
  { id: 'custom',     label: 'Personalizado' },
];

export const PeriodSelector: React.FC<Props> = ({
  selectedPeriod,
  onSelectPeriod,
  customFrom = '',
  customTo = '',
  isComparing,
  onToggleCompare,
  onRefresh,
  isRefreshing = false,
}) => {
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [fromDate, setFromDate] = useState(customFrom || new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(customTo || new Date().toISOString().slice(0, 10));

  const handleCustomApply = () => {
    onSelectPeriod('custom', fromDate, toDate);
    setShowCustomModal(false);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-surface p-3 rounded-2xl border border-border shadow-xs">
      {/* Chips de periodo */}
      <div className="flex flex-wrap items-center gap-1.5">
        {PERIOD_OPTIONS.map((opt) => {
          const isSelected = selectedPeriod === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                if (opt.id === 'custom') {
                  setShowCustomModal(true);
                } else {
                  onSelectPeriod(opt.id);
                }
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-xl transition cursor-pointer ${
                isSelected
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-muted/70 text-text-muted hover:bg-muted hover:text-text'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Controles laterales: Comparar, Refrescar e Imprimir */}
      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-text-muted cursor-pointer select-none px-2 py-1 rounded-lg hover:bg-muted/50">
          <input
            type="checkbox"
            checked={isComparing}
            onChange={(e) => onToggleCompare(e.target.checked)}
            className="rounded border-border text-emerald-600 focus:ring-emerald-500 w-3.5 h-3.5 cursor-pointer"
          />
          <span>vs periodo anterior</span>
        </label>

        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            title="Actualizar datos"
            className="p-1.5 rounded-lg border border-border text-text-muted hover:text-text hover:bg-muted transition cursor-pointer"
          >
            <RefreshCw size={14} className={isRefreshing ? 'animate-spin text-emerald-600' : ''} />
          </button>
        )}

        <button
          type="button"
          onClick={handlePrint}
          title="Imprimir / Exportar a PDF"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-border bg-surface text-text hover:bg-muted transition cursor-pointer"
        >
          <Printer size={13} />
          <span className="hidden sm:inline">Imprimir PDF</span>
        </button>
      </div>

      {/* Modal/Pop para rango personalizado */}
      {showCustomModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-surface rounded-2xl border border-border p-5 max-w-sm w-full shadow-xl">
            <h3 className="text-base font-bold text-text mb-3 flex items-center gap-2">
              <Calendar size={18} className="text-emerald-600" />
              Rango personalizado
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase mb-1">Fecha Inicial</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase mb-1">Fecha Final</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCustomModal(false)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg text-text-muted hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCustomApply}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
              >
                Aplicar rango
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
